<template>
  <ui-spinner
    v-bind="{ ...loadingProgressOptions }"
    :class="[...extraClasses]"
  />
</template>

<script>
import propValidator from '@/acv/utilities/propValidator';

export default {
  name: 'AcvProgressIndicator',
  props: {
    /**
     * Options: `primary`, `secondary`, `tertiary`, `informational`
     */
    variant: {
      type: String,
      default: 'primary',
      validator(variant) {
        return propValidator(variant, [
          'primary',
          'secondary',
          'tertiary',
          'informational',
        ]);
      },
    },
    /**
     * Sets a predefined render mode with default options.
     * Options can be overridden via props.
     */
    mode: {
      type: String,
      default: 'circular',
      validator(variant) {
        return propValidator(variant, ['circular', 'linear']);
      },
    },
    /**
     * `counterClockwise` controls the direction of the spin/progress bar
     */
    counterClockwise: {
      type: Boolean,
      default: false,
    },
    /**
     * The amount in seconds it takes for the indeterminate progress bar to fill up
     */
    fillDuration: {
      type: [String, Number],
      default: '1',
    },
    height: {
      type: [String, Number],
      default: '0',
    },
    /**
     * Show/hide the track the progress bar follows
     */
    hideBackground: {
      type: Boolean,
      default: false,
    },
    /**
     * A floating number between 0 - 1 the indicates the current progress of the indicator.
     */
    progress: {
      type: Number,
      default: 0,
      validator(value) {
        return value >= -1 && value <= 1;
      },
    },
    /**
     * Enables a rotation animation on the progress indicator
     */
    rotate: {
      type: Boolean,
      default: false,
    },
    /**
     * Controls the speed when rotate = `true`
     */
    rotationDuration: {
      type: [String, Number],
      default: 1,
    },
    shape: {
      type: String,
      default: 'circle',
      validator(variant) {
        return propValidator(variant, ['circle', 'line']);
      },
    },
    size: {
      type: [String, Number],
      default: '100',
    },
    width: {
      type: [String, Number],
      default: 0,
    },
    /**
     * The stroke size in px. Only supports up to 20px by default. For larger sizes,
     * use the following CSS selector to override:
       ```css
       .vue-progress-path path {
          stroke-width: 12;
        }
        ```
     */
    stroke: {
      type: [String, Number],
      default: 5,
      validator(value) {
        return value >= 1 && value <= 15;
      },
    },
  },
  data: () => ({
    modes: {
      circular: {
        fillDuration: 1,
        rotate: true,
        rotationDuration: 1,
        shape: 'circle',
      },
      linear: {
        fillDuration: 1,
        rotate: false,
        rotationDuration: undefined,
        shape: 'line',
      },
    },
  }),
  computed: {
    loadingProgressOptions() {
      const defaults = {
        counterClockwise: this.counterClockwise,
        fillDuration: this.fillDuration,
        height: this.height,
        hideBackground: this.hideBackground,
        progress: this.progress,
        rotate: this.rotate,
        rotationDuration: this.rotationDuration,
        shape: this.shape,
        size: this.size,
        width: this.width,
      };
      return {
        ...defaults,
        ...this.$props,
        ...this.modes[this.mode],
      };
    },
    extraClasses() {
      const extraClasses = [
        `acv-progress-indicator--${this.variant}`,
        `acv-progress-indicator--${this.shape}`,
        `stroke-${this.stroke}`,
      ];

      return [...extraClasses];
    },
  },
};
</script>

<style lang="scss" scoped>
.acv-progress-indicator {
  &--primary {
    &.vue-progress-path {
      :deep() {
        .progress {
          stroke: theme('colors.primary.base');
        }

        .background {
          stroke: theme('colors.orange.380');
        }
      }
    }
  }

  &--secondary {
    &.vue-progress-path {
      :deep() {
        .progress {
          stroke: theme('colors.white.DEFAULT');
        }

        .background {
          stroke: theme('colors.white.380');
        }
      }
    }

    :deep() {
      .mdc-circular-progress__indeterminate-circle-graphic {
        stroke: theme('colors.white.DEFAULT');
      }
    }
  }

  &--tertiary {
    &.vue-progress-path {
      :deep() {
        .progress {
          stroke: theme('colors.grey.light-x');
        }

        .background {
          stroke: theme('colors.gray.dark');
        }
      }
    }
  }

  &--informational {
    &.vue-progress-path {
      :deep() {
        .progress {
          stroke: theme('colors.secondary.light');
        }

        .background {
          stroke: theme('colors.secondary.base');
        }
      }
    }
  }

  &--primary,
  &--secondary,
  &--tertiary {
    &.vue-progress-path {
      @for $i from 1 through 20 {
        &.stroke-#{$i} {
          :deep() {
            path {
              stroke-width: $i;
            }
          }
        }
      }
    }
  }
}
</style>
